import React, { PropTypes } from 'react';
import { Header, Form, Segment, Button } from 'semantic-ui-react';
import Datepicker from 'common/components/Datepicker';
import Dropdown from 'common/components/Dropdown';
import cx from 'classnames';
import moment from 'moment';
import { DATE_FORMAT } from 'common/components/PreplanificationSelection';
import {
  paymentConditionOptions,
  invoicePaymentFrequencyOptions,
} from '../../index';

const BalanceInvoice = ({
  onValueChange,
  balanceInvoiceCreateAction,
  balanceInvoiceDate,
  balanceInvoicePaymentDate,
  balanceInvoicePaymentCondition,
  balanceInvoicePaymentFrequency,
  initialDuration,
  errors,
}) => {
  const balanceInvoiceTypeLabel = () => {
    if (initialDuration) {
      if (initialDuration === 1) {
        return `Installation`;
      } else if (initialDuration === 2) {
        return `Installation et 1 an de maintenance`;
      } else {
        return `Installation et ${initialDuration - 1} ans de maintenance`;
      }
    } else {
      return `Installation`;
    }
  };

  const isBalancePaymentDateDisable = () => {
    const selectedValue = paymentConditionOptions.find(
      r => r.value === balanceInvoicePaymentCondition
    );

    if (selectedValue) {
      return selectedValue.inputDateDisabled;
    }
    return false;
  };

  const itemBalanceInvoicePaymentConditionClassName = value => {
    const active = balanceInvoicePaymentCondition === value;
    return cx('item', {
      active: active,
      selected: active,
    });
  };

  const renderBalanceInvoicePaymentConditionDefault = () => {
    const selectedValue = paymentConditionOptions.find(
      r => r.value === balanceInvoicePaymentCondition
    );
    if (selectedValue) {
      return <div className="text">{selectedValue.text}</div>;
    }
    return <div className="default text" />;
  };

  const renderBalanceInvoicePaymentConditionItem = item => (
    <div
      key={item.value}
      data-value={item.value}
      className={itemBalanceInvoicePaymentConditionClassName(item.value)}
    >
      {item.text}
    </div>
  );

  const itemBalanceInvoicePaymentFrequencyClassName = value => {
    const active = balanceInvoicePaymentFrequency === value;
    return cx('item', {
      active: active,
      selected: active,
    });
  };

  const renderBalanceInvoicePaymentFrequencyDefault = () => {
    const selectedValue = invoicePaymentFrequencyOptions.find(
      r => r.value === balanceInvoicePaymentFrequency
    );
    if (selectedValue) {
      return <div className="text">{selectedValue.text}</div>;
    }
    return <div className="default text" />;
  };

  const renderBalanceInvoicePaymentFrequencyItem = item => (
    <div
      key={item.value}
      data-value={item.value}
      className={itemBalanceInvoicePaymentFrequencyClassName(item.value)}
    >
      {item.text}
    </div>
  );

  const updateBalancePaymentDate = (date, condition) => {
    let updatedDate = moment(date, DATE_FORMAT).add(
      'days',
      condition.numberOfDays
    );

    if (condition.endOfMonth) {
      updatedDate.endOf('month');
    }

    onValueChange(
      'invoicingInformations.balanceInvoicePaymentDate',
      updatedDate.format(DATE_FORMAT)
    );
  };

  const onBalanceDropdownChange = (value, text, choice) => {
    onValueChange(
      'invoicingInformations.balanceInvoicePaymentCondition',
      value
    );
    const selectedValue = paymentConditionOptions.find(r => r.value === value);
    if (
      selectedValue &&
      selectedValue.value !== 'CUSTOM' &&
      balanceInvoiceDate
    ) {
      updateBalancePaymentDate(balanceInvoiceDate, selectedValue);
    }
  };

  const onBalanceInvoicePaymentFrequencyDropdownChange = (
    value,
    text,
    choice
  ) => {
    onValueChange(
      'invoicingInformations.balanceInvoicePaymentFrequency',
      parseInt(value, 0)
    );
  };

  const fieldClass = fieldName =>
    cx('field required', {
      error: errors.includes(fieldName),
    });

  const invoiceDateLabel = isCreateAction => {
    return isCreateAction ? "Date d'émission estimée" : "Date d'émission";
  };

  const invoicePaymentDateLabel = isCreateAction => {
    return isCreateAction ? 'Date de réglement estimée' : 'Date de réglement';
  };

  const onBalanceInvoiceDateChange = date => {
    onValueChange(
      'invoicingInformations.balanceInvoiceDate',
      moment(date.date).format(DATE_FORMAT)
    );
    const selectedValue = paymentConditionOptions.find(
      r => r.value === balanceInvoicePaymentCondition
    );
    if (selectedValue && selectedValue.value !== 'CUSTOM') {
      updateBalancePaymentDate(
        moment(date.date).format(DATE_FORMAT),
        selectedValue.numberOfDays
      );
    }
  };

  const onBalanceInvoicePaymentDateChange = date => {
    onValueChange(
      'invoicingInformations.balanceInvoicePaymentDate',
      moment(date.date).format(DATE_FORMAT)
    );
  };

  const onBalanceCreateInvoiceActionChange = event => {
    onValueChange(
      'invoicingInformations.balanceInvoiceCreateAction',
      event.target.value === 'true'
    );
    if (event.target.value !== 'true') {
      onValueChange(
        'invoicingInformations.balanceInvoiceDate',
        moment().format(DATE_FORMAT)
      );
      const selectedValue = paymentConditionOptions.find(
        r => r.value === balanceInvoicePaymentCondition
      );
      if (selectedValue && selectedValue.value !== 'CUSTOM') {
        updateBalancePaymentDate(
          moment().format(DATE_FORMAT),
          selectedValue.numberOfDays
        );
      }
    }
  };

  return (
    <Segment>
      <Header size="tiny">5- Préparer la facture de solde</Header>
      <Form.Group widths="equal">
        <Form.Field required>
          <label>A facturer la première année</label>
          {balanceInvoiceTypeLabel()}
        </Form.Field>
        <Form.Field required>
          <label>Payable</label>
          <Dropdown
            name="balanceInvoicePaymentFrequency"
            value={balanceInvoicePaymentFrequency}
            items={invoicePaymentFrequencyOptions}
            renderDefault={renderBalanceInvoicePaymentFrequencyDefault}
            renderItem={renderBalanceInvoicePaymentFrequencyItem}
            onChange={onBalanceInvoicePaymentFrequencyDropdownChange}
          />
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field required>
          <label>Action à appliquer sur la prochaine facture</label>
          <Button.Group>
            <Button
              type="button"
              positive={balanceInvoiceCreateAction}
              value={true}
              onClick={onBalanceCreateInvoiceActionChange}
            >
              Créer
            </Button>
            <Button
              type="button"
              positive={!balanceInvoiceCreateAction}
              value={false}
              onClick={onBalanceCreateInvoiceActionChange}
            >
              Emettre
            </Button>
          </Button.Group>
        </Form.Field>
        <Form.Field
          required
          className={fieldClass('invoicingInformations.balanceInvoiceDate')}
        >
          <label>{invoiceDateLabel(balanceInvoiceCreateAction)}</label>
          <Datepicker
            identifier="balanceInvoiceDate"
            name="balanceInvoiceDate"
            value={balanceInvoiceDate}
            onChange={onBalanceInvoiceDateChange}
            placeholder="JJ/MM/AAAA"
            disabled={!balanceInvoiceCreateAction}
          />
        </Form.Field>
        <Form.Field required>
          <label>Conditions de règlement</label>
          <Dropdown
            name="balanceInvoicePaymentCondition"
            value={balanceInvoicePaymentCondition}
            items={paymentConditionOptions}
            renderDefault={renderBalanceInvoicePaymentConditionDefault}
            renderItem={renderBalanceInvoicePaymentConditionItem}
            onChange={onBalanceDropdownChange}
          />
        </Form.Field>
        <Form.Field
          required
          className={fieldClass(
            'invoicingInformations.balanceInvoicePaymentDate'
          )}
        >
          <label>{invoicePaymentDateLabel(balanceInvoiceCreateAction)}</label>
          <Datepicker
            identifier="balanceInvoicePaymentDate"
            name="balanceInvoicePaymentDate"
            value={balanceInvoicePaymentDate}
            onChange={onBalanceInvoicePaymentDateChange}
            placeholder="JJ/MM/AAAA"
            disabled={isBalancePaymentDateDisable()}
          />
        </Form.Field>
      </Form.Group>
    </Segment>
  );
};

BalanceInvoice.propTypes = {
  balanceInvoiceCreateAction: PropTypes.bool.isRequired,
  balanceInvoiceDate: PropTypes.string,
  balanceInvoicePaymentDate: PropTypes.string,
  balanceInvoicePaymentCondition: PropTypes.string.isRequired,
  balanceInvoicePaymentFrequency: PropTypes.number.isRequired,
  errors: PropTypes.array,
};

export default BalanceInvoice;



// WEBPACK FOOTER //
// ./src/modules/saas/aast/case-contract/organize/components/BalanceInvoice.js